import { useEffect, useState } from "react"
import type { UseQueryResult } from "@tanstack/react-query"

import { ErrorDisplay } from "../../../../../core/api/errorTypes"
import { useApiErrorHandling } from "../../../../../core/api/useApiErrorHandling"

export type RefreshableQuery = Pick<UseQueryResult<unknown>, "refetch" | "dataUpdatedAt">

// a row missing from the fresh list stays as it was, so the selection doesn't shrink silently
export function withFreshRows<TRow extends Readonly<{ id: number }>>(
    rows: ReadonlyArray<TRow>,
    freshRows: ReadonlyArray<TRow> | null | undefined,
) {
    const freshById = new Map<number, TRow>(freshRows?.map((row) => [row.id, row]))

    return rows.map((row) => freshById.get(row.id) ?? row)
}

export function useRefreshBeforeExport(query: RefreshableQuery, runExport: () => void) {
    const [isExporting, setIsExporting] = useState(false)
    const [refreshedAt, setRefreshedAt] = useState<number>()
    const { handleError } = useApiErrorHandling()

    // refetch can resolve before the render that holds the fresh rows
    useEffect(() => {
        if (refreshedAt === undefined || query.dataUpdatedAt < refreshedAt) return

        setRefreshedAt(undefined)
        setIsExporting(false)

        // thrown from an effect, it would replace the page with the route error screen
        try {
            runExport()
        } catch (error) {
            handleError(error, ErrorDisplay.SNACKBAR)
        }
    }, [refreshedAt, query.dataUpdatedAt])

    const exportFresh = async () => {
        if (isExporting) return

        setIsExporting(true)
        const { isError, error, dataUpdatedAt } = await query.refetch()

        if (isError) {
            handleError(error, ErrorDisplay.SNACKBAR)
        }

        // 0: filters changed during the refresh and the new data isn't loaded yet
        if (isError || !dataUpdatedAt) {
            setIsExporting(false)
            return
        }

        setRefreshedAt(dataUpdatedAt)
    }

    return { exportFresh, isExporting }
}
